import React from 'react'
import { Inline } from '@airbus/components-react/components'
import { SAISIE_HORAIRE } from 'constants/constants'
import FormNumericInput from 'components/FormFields/FormNumericInput'
import { parse, isAfter, isEqual } from 'date-fns'
import { FieldErrors } from 'react-hook-form'
import { FlightDetailInput, NonStandardAgeingDetail, NonStandardAgeingDetails } from 'types/techlog'
import FormDuration from 'components/FormFields/FormDuration'

type Props = {
  rmdate: Date
  formatDuration: string
  nonStandardAgeingDetails: NonStandardAgeingDetails
  readOnly: boolean
  activeStep: number
  errors: FieldErrors<FlightDetailInput>
}

/**
 * Shows up only non-outdated NSAD based on Techlog's date
 */
const NSAD = ({ rmdate, formatDuration, nonStandardAgeingDetails, readOnly, activeStep, errors }: Props) => {
  const isAfterOrEqual = (date1: Date, date2: Date) => {
    return isAfter(date1, date2) || isEqual(date1, date2)
  }

  /**
   * Max value of a (vitylng, vitydec) number:
   * ex.: max value for a (6,2) number is 9999,99
   * @param nsad
   */
  const nsadMaxValue = (nsad: NonStandardAgeingDetail) => {
    const totalPart = nsad?.vitylng
    const decimalPart = nsad?.vitydec
    if (totalPart !== undefined && decimalPart !== undefined) {
      const intPart = totalPart - decimalPart
      return Number('999999999999'.slice(-intPart) + (decimalPart > 0 ? '0.' + '00000001'.slice(-decimalPart) : ''))
    }
    return Number('9.99')
  }

  /**
   * Step for a (vitylng, vitydec) number is made of the decimal part only:
   * ex.: step for a (6,2) number is 0,01
   * @param nsad
   */
  const nsadStep = (nsad: NonStandardAgeingDetail) => {
    const decimalPart = nsad?.vitydec
    if (decimalPart !== undefined && decimalPart > 0) {
      return Number('0.' + '00000001'.slice(-decimalPart))
    } else {
      return 1
    }
  }

  return (
    <>
      <Inline spacing='2-x' style={{ padding: '0 1rem', marginBottom: '1rex' }}>
        {nonStandardAgeingDetails &&
          Object.keys(nonStandardAgeingDetails).map((nbAtypique, i) =>
            nonStandardAgeingDetails[nbAtypique] !== null &&
            !['5', '6', '7'].includes(nonStandardAgeingDetails[nbAtypique]?.tycal as string) &&
            nonStandardAgeingDetails[nbAtypique]?.vitytyp === 'H' &&
            isAfterOrEqual(
              rmdate,
              parse(nonStandardAgeingDetails[nbAtypique]?.startDate as string, 'dd/MM/yyyy', new Date()),
            ) &&
            formatDuration === SAISIE_HORAIRE.HEURES_MINUTES ? (
              <FormDuration
                label={nonStandardAgeingDetails[nbAtypique]?.vitycod}
                name={'vocpt' + nbAtypique}
                key={'vocpt' + nbAtypique}
                pattern='[0-9][0-9]:[0-5][0-9]'
                rules={{
                  required: nonStandardAgeingDetails[nbAtypique]?.tycal === '0',
                  validate: (d: string) => d.match('[0-9][0-9]:[0-5][0-9]') !== null,
                }}
                error={Boolean(errors[`vocpt${nbAtypique}` as keyof typeof errors])}
                readOnly={
                  readOnly || activeStep === 2 || ['1'].includes(nonStandardAgeingDetails[nbAtypique]?.tycal as string)
                }
              />
            ) : (
              nonStandardAgeingDetails[nbAtypique] !== null &&
              !['5', '6', '7'].includes(nonStandardAgeingDetails[nbAtypique]?.tycal as string) &&
              isAfterOrEqual(
                rmdate,
                parse(nonStandardAgeingDetails[nbAtypique]?.startDate as string, 'dd/MM/yyyy', new Date()),
              ) && (
                <FormNumericInput
                  label={nonStandardAgeingDetails[nbAtypique]?.vitycod}
                  name={'vocpt' + nbAtypique}
                  key={'vocpt' + nbAtypique}
                  rules={{
                    required: nonStandardAgeingDetails[nbAtypique]?.tycal === '0',
                  }}
                  minValue={0}
                  maxValue={nsadMaxValue(nonStandardAgeingDetails[nbAtypique])}
                  step={nsadStep(nonStandardAgeingDetails[nbAtypique])}
                  error={Boolean(errors[`vocpt${nbAtypique}` as keyof typeof errors])}
                  readOnly={
                    readOnly ||
                    activeStep === 2 ||
                    ['1'].includes(nonStandardAgeingDetails[nbAtypique]?.tycal as string)
                  }
                />
              )
            ),
          )}
      </Inline>
    </>
  )
}

export default NSAD
